import * as THREE from 'three';
import { cloudFieldGLSL, cloudUniforms } from './clouds';

// DISTANT RAIN CURTAINS: soft translucent rain SHAFTS hanging from the cloud base toward the ground, far across
// the landscape, so a storm reads as "raining over there" -- cells under the cloud banks, like a real squall
// across a bay -- not just the particle column around the player. A pool of big vertical billboards on a ring
// around the player; each samples the SHARED cloud field at its own world spot and only shows where the cloud
// is dense AND a storm is active, so the cells appear, fade, and drift with the clouds. One instanced draw,
// client-only, purely cosmetic. (The near streaks in rain.ts handle the rain right around you.)

const COUNT = 40;                      // shafts on the ring (only the cloud-covered ones show at once)
const RING_MIN = 140, RING_MAX = 460;  // distance band from the player (metres)
const W = 95;                          // shaft width (world m)

export class RainCurtains {
  readonly mesh: THREE.Mesh;
  private readonly u = {
    uTime: { value: 0 },
    uCloudTime: cloudUniforms.uTime,        // shared with the cloud sheet -> shafts sit under the clouds you see
    uCloudCover: cloudUniforms.uCloudCover,
    uStorm: cloudUniforms.uStorm,           // shared storm intensity
    uPlayer: { value: new THREE.Vector3() },
    uMidY: { value: 80 }, uHalfH: { value: 95 },
  };

  /** bottomY ~ ground/sea level the shafts reach down to; topY ~ height they hang from (toward the cloud base). */
  constructor(bottomY: number, topY: number) {
    this.u.uMidY.value = (bottomY + topY) * 0.5;
    this.u.uHalfH.value = (topY - bottomY) * 0.5;

    const geo = new THREE.InstancedBufferGeometry();
    geo.instanceCount = COUNT;
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, 1, 0]), 3));
    geo.setIndex([0, 1, 2, 0, 2, 3]);
    const ring = new Float32Array(COUNT * 4); // (offsetX, offsetZ, widthMul, phase)
    for (let i = 0; i < COUNT; i++) {
      const a = (i / COUNT) * Math.PI * 2 + Math.random() * 0.5;
      const d = RING_MIN + Math.random() * (RING_MAX - RING_MIN);
      ring[i * 4] = Math.cos(a) * d; ring[i * 4 + 1] = Math.sin(a) * d;
      ring[i * 4 + 2] = 0.7 + Math.random() * 0.9;   // width mul
      ring[i * 4 + 3] = Math.random();               // per-shaft phase (varies the streak texture)
    }
    geo.setAttribute('iRing', new THREE.InstancedBufferAttribute(ring, 4));

    const mat = new THREE.ShaderMaterial({
      uniforms: this.u,
      transparent: true,
      depthWrite: false,
      depthTest: true,                       // distant hills occlude the base of a shaft
      defines: { USE_LOGDEPTHBUF: '' },       // match the scene's log-depth so occlusion is correct
      vertexShader: `
        #include <common>
        #include <logdepthbuf_pars_vertex>
        attribute vec4 iRing;
        uniform float uTime, uCloudTime, uCloudCover, uStorm, uMidY, uHalfH;
        uniform vec3 uPlayer;
        varying vec2 vUv; varying float vVis; varying float vPhase;
        ${cloudFieldGLSL}
        void main() {
          vec2 base = uPlayer.xz + iRing.xy;                          // world XZ of this shaft (rides the player ring)
          float cloud = oxCloud(base, uCloudTime) + uCloudCover;
          float vis = smoothstep(0.50, 0.74, cloud) * clamp(uStorm, 0.0, 1.0); // only under dense cloud, in a storm
          vec3 center = vec3(base.x, uMidY, base.y);
          // yaw-billboard: face the camera horizontally, stay vertical (a standing shaft).
          vec3 toCam = normalize(cameraPosition - center);
          vec3 right = normalize(vec3(toCam.z, 0.0, -toCam.x));
          vec3 corner = center + right * position.x * (${(W * 0.5).toFixed(1)} * iRing.z) + vec3(0.0, 1.0, 0.0) * position.y * uHalfH;
          float camD = length(cameraPosition - center);
          vis *= smoothstep(80.0, 150.0, camD);                      // these are DISTANT -> fade any that get near
          gl_Position = projectionMatrix * viewMatrix * vec4(corner, 1.0);
          #include <logdepthbuf_vertex>
          vUv = position.xy * 0.5 + 0.5; vVis = vis; vPhase = iRing.w;
        }`,
      fragmentShader: `
        #include <common>
        #include <logdepthbuf_pars_fragment>
        uniform float uTime;
        varying vec2 vUv; varying float vVis; varying float vPhase;
        void main() {
          #include <logdepthbuf_fragment>
          if (vVis < 0.01) discard;
          float edgeX = smoothstep(0.0, 0.30, vUv.x) * smoothstep(1.0, 0.70, vUv.x);   // soft sides
          float gradY = smoothstep(0.04, 0.40, vUv.y) * smoothstep(1.0, 0.80, vUv.y);  // shaft body: fade ground + cloud
          // A SMOOTH soft veil (like a real distant rain shaft) -- only a very subtle, broad, low-frequency
          // vertical variation + a slow downward drift, so it reads as misty rain, never sharp speckle/noise.
          float variation = 0.86 + 0.14 * sin(vUv.x * 9.0 + vPhase * 25.0) * (0.55 + 0.45 * sin(vUv.y * 2.2 - uTime * 0.6 + vPhase * 6.0));
          float a = edgeX * gradY * vVis * 0.30 * variation;
          vec3 c = vec3(0.56, 0.61, 0.71);     // soft misty grey-blue
          gl_FragColor = vec4(c, a);
        }`,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3;           // after the clouds, before the near rain streaks (4)
    this.mesh.visible = false;
  }

  update(playerPos: THREE.Vector3, time: number): void {
    this.u.uPlayer.value.copy(playerPos);
    this.u.uTime.value = time;
    this.mesh.visible = this.u.uStorm.value > 0.005;
  }
}
